import React from 'react';
import PropTypes from 'prop-types';
import { Stack } from '@mui/material';
import { useRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import Select from 'pages/reference-implementation/components/select';
import HelpImg from 'pages/provisioning/_components/helpImg';
import { isEksAdGroupValid } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import toast from 'react-hot-toast';

const EksAdGroups = ({ value, onChange, isRequired }) => {
    const [options, setOptions] = React.useState([]);
    const [inputValue, setInputValue] = React.useState(value || '');
    const [adGroupValid, setAdGroupValid] = useRecoilState(isEksAdGroupValid);
    const isMounted = React.useRef(false);

    const adGroupsApi = useMutation(() => axios.get(`${endPoints.aws.lookups.awsEksPrivilegedAdGroup(inputValue)}`));
    const adGroupsApi2 = useMutation((payload) => axios.get(`${endPoints.aws.lookups.awsEksPrivilegedAdGroup(payload)}`));
    const adGroupValidationApi = useMutation((adGroup) => axios.get(`${endPoints.aws.lookups.awsValidateAdGroup(adGroup)}`));

    const handleOnSelectChange = (selected) => {
        onChange(selected?.label || null);
        if (selected?.label) {
            adGroupsApi2.mutate(selected?.label);
            adGroupValidationApi.mutate(selected?.label);
        } else {
            setAdGroupValid(false);
            toast.error('Please select a valid AD Group.');
        }
    };

    React.useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        if (inputValue === '') {
            setOptions([]);
            onChange(null);
            setAdGroupValid(false);
        }
        if (inputValue !== '' && inputValue.length > 2) {
            adGroupsApi.mutate();
        }
    }, [inputValue]);

    React.useEffect(() => {
        if (adGroupsApi?.isSuccess && adGroupsApi?.data?.data && inputValue !== '') {
            const adList = adGroupsApi?.data?.data?.map((adGroup) => ({
                label: adGroup?.displayName,
                value: adGroup?.displayName,
            }));
            setOptions(adList);
        }
    }, [adGroupsApi?.isSuccess]);

    React.useEffect(() => {
        if (adGroupsApi2?.isSuccess && adGroupsApi2?.data?.data) {
            if (!adGroupsApi2?.data?.data.length) {
                onChange(null);
                setAdGroupValid(false);
                toast.error('Selected AD Group is invalid.');
            }
        }
    }, [adGroupsApi2?.isSuccess]);

    React.useEffect(() => {
        if (adGroupValidationApi?.isSuccess) {
            setAdGroupValid(!!adGroupValidationApi?.data?.isSuccess);
            if (!adGroupValidationApi?.data?.isSuccess) {
                toast.error('Selected AD Group is invalid.');
            }
        }
    }, [adGroupValidationApi?.isSuccess]);

    React.useEffect(() => {
        if (adGroupValidationApi?.isError && adGroupValidationApi?.error) {
            setAdGroupValid(false);
            const errorMessage = Array.isArray(adGroupValidationApi?.error) ? adGroupValidationApi?.error : [adGroupValidationApi?.error?.message];
            toast.error(errorMessage?.join(', '));
        }
    }, [adGroupValidationApi?.isError]);

    React.useEffect(() => {
        // on edit, value is pre-populated without going through handleOnSelectChange, so validate it once on mount
        if (value && !adGroupValid) {
            adGroupValidationApi.mutate(value);
        }
        // eslint-disable-next-line
    }, []);

    const validateInput = () => (value && !adGroupValid ? 'validate' : '');

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Select
                key="eksAdGroupName"
                label="Privileged Active Directory Group *"
                value={value || null}
                options={options}
                handleOnSelect={(selected) => handleOnSelectChange(selected)}
                isLoading={adGroupsApi.isLoading}
                noOptionsText="Enter atleast 3 characters for suggestions"
                autoComplete
                includeInputInList
                filterSelectedOptions
                fullWidth
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                isRequired={isRequired}
                className={validateInput()}
            />
            <HelpImg
                title={
                    <>
                        <div>
                            AWS EKS Namespace access is granted through Active Directory groups that follow the naming convention:
                            <div>
                                <b>Team.App.AWS.EKS followed by your application name.</b>
                            </div>
                            <div>
                                <b>Example: Team.App.aws.eks.devxinfra</b>
                            </div>
                            Please ensure you select the correct Active Directory group that matches this naming format to receive the appropriate namespace
                            access.
                        </div>
                    </>
                }
            />
        </Stack>
    );
};

EksAdGroups.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    isRequired: PropTypes.bool,
};

EksAdGroups.defaultProps = {
    value: null,
    isRequired: false,
};

export default EksAdGroups;
